import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { MessageSquare, Send, Search, Clock, CheckCircle, XCircle, Plus, X } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import MessageVariables from '../components/MessageVariables';
import { messagesAPI, contactsAPI, campaignsAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { formatarDataHora } from '../utils/dateFormatter';
import { PageSkeleton, SkeletonGrid } from '../components/Skeleton';

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendFormData, setSendFormData] = useState({
    contato_id: '',
    telefone: '',
    mensagem: '',
    media_id: '',
    tipo_midia: 'texto',
    useContact: true
  });
  const messageTextareaRef = useRef(null);
  const [contactSearch, setContactSearch] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [scheduleFormData, setScheduleFormData] = useState({
    campanha_id: '',
    mensagem: '',
    ordem: 1,
    delay_segundos: 0
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { darkMode } = useTheme();

  useEffect(() => {
    loadMessages();
    loadContacts();
    loadCampaigns();

    // Fechar dropdown ao clicar fora
    const handleClickOutside = (event) => {
      if (!event.target.closest('.contact-autocomplete')) {
        setShowContactDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMessages = async () => {
    try {
      const response = await messagesAPI.getByCliente();
      const messagesData = response.data?.messages || response.data || [];
      console.log('Array de mensagens:', messagesData);
      console.log('Primeira mensagem (exemplo):', messagesData[0]);
      setMessages(messagesData);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await contactsAPI.list(user.cliente_id);
      setContacts(response.data.messages || []);
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await campaignsAPI.list();
      setCampaigns(response.data?.campanhas || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    try {
      const messageData = {
        cliente_id: user.cliente_id,
        usuario_id: user.id,
        mensagem: sendFormData.mensagem,
        tipo_midia: sendFormData.tipo_midia
      };

      // Adicionar contato_id ou telefone
      if (sendFormData.useContact) {
        messageData.contato_id = sendFormData.contato_id;
      } else {
        messageData.telefone = sendFormData.telefone;
      }

      // Adicionar media_id se houver
      if (sendFormData.media_id) {
        messageData.media_id = sendFormData.media_id;
      }

      await messagesAPI.send(messageData);
      toast.success('Mensagem enviada com sucesso!');
      setShowSendModal(false);
      setSendFormData({
        contato_id: '',
        telefone: '',
        mensagem: '',
        media_id: '',
        tipo_midia: 'texto',
        useContact: true
      });
      loadMessages();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao enviar mensagem');
    }
  };

  const handleScheduleMessage = async (e) => {
    e.preventDefault();

    try {
      const messageData = {
        ...scheduleFormData,
        cliente_id: user.cliente_id,
        usuario_id: user.id
      };

      await messagesAPI.schedule(messageData);
      toast.success('Mensagem agendada com sucesso!');
      setShowScheduleModal(false);
      setScheduleFormData({
        campanha_id: '',
        mensagem: '',
        ordem: 1,
        delay_segundos: 0
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao agendar mensagem');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'enviada': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pendente': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'erro': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const filteredMessages = Array.isArray(messages) ? messages.filter(message =>
    (message.text || message.mensagem)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (message.telefone !== 'N/A' && message.telefone?.includes(searchTerm)) ||
    (message.nome !== 'N/A' && message.nome?.toLowerCase().includes(searchTerm.toLowerCase()))
  ) : [];

  // Agrupar mensagens por contato
  const groupedMessages = filteredMessages.reduce((groups, message) => {
    const key = message.telefone || 'sem-telefone';
    if (!groups[key]) {
      groups[key] = {
        telefone: message.telefone,
        nome: message.nome,
        messages: [],
        lastMessage: null,
        totalMessages: 0
      };
    }
    groups[key].messages.push(message);
    groups[key].totalMessages++;
    if (!groups[key].lastMessage || new Date(message.create_at).getTime() > new Date(groups[key].lastMessage.create_at).getTime()) {
      groups[key].lastMessage = message;
    }
    return groups;
  }, {});

  const contactGroups = Object.values(groupedMessages).sort((a, b) =>
    new Date(b.lastMessage.create_at).getTime() - new Date(a.lastMessage.create_at).getTime()
  );
  if (loading) {
    return (
      <Layout>
        <PageSkeleton title="Mensagens" subtitle="Envie mensagens e visualize histórico">
          <SkeletonGrid itens={6} colunas={2} />
        </PageSkeleton>
      </Layout>
    );
  }


  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader title="Mensagens" subtitle="Envie mensagens e visualize histórico">
            <button
              onClick={() => setShowSendModal(true)}
              className="gradient-button inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white"
            >
              <Send className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Enviar Mensagem</span>
            </button>
            <button
              onClick={() => setShowScheduleModal(true)}
              className="secondary-button inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Agendar Mensagem</span>
            </button>
        </PageHeader>

        {/* Search */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
          </div>
          <input
            type="text"
            placeholder="Buscar mensagens..."
            className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 focus:outline-none focus:ring-1 focus:ring-nomoz-orange focus:border-nomoz-orange ${
              darkMode
                ? 'bg-zinc-800 border-gray-700 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
          {/* Total Enviadas */}
          <div className={`rounded-lg shadow ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                  <Send className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} truncate`}>
                      Total Enviadas
                    </dt>
                    <dd className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {filteredMessages.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Entregues */}
          <div className={`rounded-lg shadow ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} truncate`}>
                      Entregues
                    </dt>
                    <dd className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {filteredMessages.filter(m => m.status === 'enviada').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Pendentes */}
          <div className={`rounded-lg shadow ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-yellow-100 rounded-md p-3">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} truncate`}>
                      Pendentes
                    </dt>
                    <dd className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {filteredMessages.filter(m => m.status === 'pendente').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Erros */}
          <div className={`rounded-lg shadow ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-red-100 rounded-md p-3">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} truncate`}>
                      Erros
                    </dt>
                    <dd className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {filteredMessages.filter(m => m.status === 'erro').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contacts List */}
        {contactGroups.length === 0 ? (
          <div className={`text-center py-12 rounded-lg ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <MessageSquare className={`mx-auto h-12 w-12 ${darkMode ? 'text-gray-400' : 'text-gray-300'} mb-4`} />
            <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Nenhuma mensagem
            </h3>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Comece enviando uma mensagem.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {contactGroups.map((contact) => (
              <div
                key={contact.telefone}
                className={`rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer ${
                  darkMode ? 'bg-zinc-800 border border-gray-700 hover:bg-zinc-700' : 'bg-white border border-gray-200 shadow hover:bg-gray-50'
                }`}
                onClick={() => {
                  setSelectedContact(contact);
                  setShowDetailsModal(true);
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                        darkMode ? 'bg-blue-900/20' : 'bg-blue-100'
                      }`}>
                        <MessageSquare className={`h-6 w-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <p className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {contact.nome !== 'N/A' ? contact.nome : 'Contato'}
                        </p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          darkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {contact.totalMessages} mensagem{contact.totalMessages !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'} truncate`}>
                        Última: {contact.lastMessage.text}
                      </p>
                      <div className={`flex items-center space-x-4 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span>
                          📱 {contact.telefone !== 'N/A' ? contact.telefone : 'Não informado'}
                        </span>
                        <span>
                          🕒 {formatarDataHora(contact.lastMessage.create_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Clique para ver detalhes
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Send Message Modal */}
        {showSendModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 transition-opacity">
            <div className={`relative top-10 mx-auto p-6 w-full max-w-2xl shadow-lg rounded-lg ${
              darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-6">
                  <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Enviar Mensagem
                  </h3>
                  <button
                    onClick={() => {
                      setShowSendModal(false);
                      setSendFormData({
                        contato_id: '',
                        telefone: '',
                        mensagem: '',
                        media_id: '',
                        tipo_midia: 'texto',
                        useContact: true
                      });
                      setContactSearch('');
                      setShowContactDropdown(false);
                    }}
                    className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <form onSubmit={handleSendMessage} className="space-y-4">
                  <div>
                    <div className="flex space-x-4 mb-3">
                      <label className={`flex items-center ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                        <input
                          type="radio"
                          checked={sendFormData.useContact}
                          onChange={() => setSendFormData({...sendFormData, useContact: true, telefone: ''})}
                          className="mr-2"
                        />
                        Usar contato da lista
                      </label>
                      <label className={`flex items-center ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                        <input
                          type="radio"
                          checked={!sendFormData.useContact}
                          onChange={() => setSendFormData({...sendFormData, useContact: false, contato_id: ''})}
                          className="mr-2"
                        />
                        Telefone direto
                      </label>
                    </div>

                    {sendFormData.useContact ? (
                      <div className="relative contact-autocomplete">
                        <label className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                          Contato
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="Digite o nome ou telefone do contato..."
                          className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                            darkMode
                              ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          }`}
                          value={contactSearch}
                          onChange={(e) => {
                            setContactSearch(e.target.value);
                            setShowContactDropdown(true);
                          }}
                          onFocus={() => setShowContactDropdown(true)}
                        />
                        {showContactDropdown && (
                          <div className={`absolute z-10 mt-1 w-full rounded-md shadow-lg max-h-60 overflow-auto ${
                            darkMode
                              ? 'bg-zinc-700 border border-gray-600'
                              : 'bg-white border border-gray-300'
                          }`}>
                            {Array.isArray(contacts) && contacts
                              .filter(contact =>
                                contact.nome?.toLowerCase().includes(contactSearch.toLowerCase()) ||
                                contact.telefone?.includes(contactSearch)
                              )
                              .slice(0, 10)
                              .map(contact => (
                                <div
                                  key={contact.id}
                                  className={`px-3 py-2 cursor-pointer border-b last:border-b-0 ${
                                    darkMode
                                      ? 'border-gray-600 hover:bg-zinc-600'
                                      : 'border-gray-100 hover:bg-gray-100'
                                  }`}
                                  onClick={() => {
                                    setSendFormData({...sendFormData, contato_id: contact.id});
                                    setContactSearch(`${contact.nome} - ${contact.telefone}`);
                                    setShowContactDropdown(false);
                                  }}
                                >
                                  <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {contact.nome}
                                  </div>
                                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {contact.telefone}
                                  </div>
                                </div>
                              ))
                            }
                            {contactSearch && contacts.filter(contact =>
                              contact.nome?.toLowerCase().includes(contactSearch.toLowerCase()) ||
                              contact.telefone?.includes(contactSearch)
                            ).length === 0 && (
                              <div className={`px-3 py-2 text-sm ${
                                darkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                Nenhum contato encontrado
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                          Telefone
                        </label>
                        <input
                          type="tel"
                          required
                          placeholder="11999999999"
                          className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                            darkMode
                              ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          }`}
                          value={sendFormData.telefone}
                          onChange={(e) => setSendFormData({...sendFormData, telefone: e.target.value})}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                      Mensagem
                    </label>

                    {/* Componente de variáveis */}
                    <div className="mb-3">
                      <MessageVariables
                        textareaRef={messageTextareaRef}
                        onInsertVariable={(newText) => {
                          setSendFormData({...sendFormData, mensagem: newText});
                        }}
                      />
                    </div>

                    <textarea
                      ref={messageTextareaRef}
                      required
                      className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      rows={6}
                      value={sendFormData.mensagem}
                      onChange={(e) => setSendFormData({...sendFormData, mensagem: e.target.value})}
                      placeholder="Digite sua mensagem... Use variáveis como {{nome}}, {{telefone}}, etc."
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                      Tipo de Mídia
                    </label>
                    <select
                      className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      value={sendFormData.tipo_midia}
                      onChange={(e) => setSendFormData({...sendFormData, tipo_midia: e.target.value})}
                    >
                      <option value="texto">Apenas Texto</option>
                      <option value="imagem">Imagem</option>
                      <option value="video">Vídeo</option>
                      <option value="documento">Documento</option>
                    </select>
                  </div>

                  {sendFormData.tipo_midia !== 'texto' && (
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                        Arquivo de Mídia
                      </label>
                      <input
                        type="file"
                        accept={sendFormData.tipo_midia === 'imagem' ? 'image/*' : sendFormData.tipo_midia === 'video' ? 'video/*' : '*/*'}
                        className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                          darkMode
                            ? 'bg-zinc-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        onChange={(e) => {
                          // Aqui você pode implementar upload da mídia
                          console.log('Arquivo selecionado:', e.target.files[0]);
                        }}
                      />
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Selecione um arquivo para enviar junto com a mensagem
                      </p>
                    </div>
                  )}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowSendModal(false);
                        setSendFormData({
                          contato_id: '',
                          telefone: '',
                          mensagem: '',
                          media_id: '',
                          tipo_midia: 'texto',
                          useContact: true
                        });
                        setContactSearch('');
                        setShowContactDropdown(false);
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nomoz-orange ${
                        darkMode
                          ? 'border border-gray-600 text-gray-300 bg-zinc-700 hover:bg-zinc-600'
                          : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="gradient-button px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nomoz-orange"
                    >
                      Enviar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Message Modal */}
        {showScheduleModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 transition-opacity">
            <div className={`relative top-20 mx-auto p-5 w-96 shadow-lg rounded-md ${
              darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
              <div className="mt-3">
                <h3 className={`text-lg font-medium mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Agendar Mensagem para Campanha
                </h3>
                <form onSubmit={handleScheduleMessage} className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                      Campanha
                    </label>
                    <select
                      required
                      className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      value={scheduleFormData.campanha_id}
                      onChange={(e) => setScheduleFormData({...scheduleFormData, campanha_id: e.target.value})}
                    >
                      <option value="">Selecione uma campanha...</option>
                      {Array.isArray(campaigns) && campaigns.map(campaign => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                      Mensagem
                    </label>
                    <textarea
                      required
                      className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      rows={4}
                      value={scheduleFormData.mensagem}
                      onChange={(e) => setScheduleFormData({...scheduleFormData, mensagem: e.target.value})}
                      placeholder="Digite sua mensagem..."
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                      Ordem
                    </label>
                    <input
                      type="number"
                      min="1"
                      className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      value={scheduleFormData.ordem}
                      onChange={(e) => setScheduleFormData({...scheduleFormData, ordem: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                      Delay (segundos)
                    </label>
                    <input
                      type="number"
                      min="0"
                      className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      value={scheduleFormData.delay_segundos}
                      onChange={(e) => setScheduleFormData({...scheduleFormData, delay_segundos: parseInt(e.target.value)})}
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowScheduleModal(false);
                        setScheduleFormData({
                          campanha_id: '',
                          mensagem: '',
                          ordem: 1,
                          delay_segundos: 0
                        });
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nomoz-orange ${
                        darkMode
                          ? 'border border-gray-600 text-gray-300 bg-zinc-700 hover:bg-zinc-600'
                          : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="secondary-button px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nomoz-orange"
                    >
                      Agendar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedContact && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className={`relative top-10 mx-auto p-6 w-full max-w-4xl shadow-lg rounded-lg ${
              darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Mensagens - {selectedContact.nome !== 'N/A' ? selectedContact.nome : 'Contato'}
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="max-h-96 overflow-y-auto space-y-4">
                {selectedContact.messages.map((message) => (
                  <div key={message.id} className={`p-4 rounded-lg ${
                    darkMode ? 'bg-zinc-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          {getStatusIcon(message.status)}
                          <span className={`text-xs font-medium ${
                            message.status === 'enviada' ? 'text-green-600' :
                            message.status === 'pendente' ? 'text-yellow-600' :
                            message.status === 'erro' ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {message.status || 'Desconhecido'}
                          </span>
                        </div>
                        <p className={`text-sm mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          {message.text}
                        </p>
                        <div className={`flex items-center space-x-4 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span>
                            🕒 {formatarDataHora(message.create_at)}
                          </span>
                          {message.media_id && (
                            <span className={darkMode ? 'text-blue-400' : 'text-blue-600'}>📎 Mídia</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Messages;